/**
 * 도장 관리 패널 — user-contract-management 트리 안에 인라인 렌더 + 단독 라우트 양쪽에서 사용
 */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  generateSealDataUrl,
  tintCanvasToSealColor,
  type SealShape,
} from "%/sealCanvas";
import {
  IconLock,
  IconSparkle,
  IconScribble,
  IconImage,
  IconTrash,
  IconPlus,
} from "./icons";
import {
  useSealList,
  useCreateSeal,
  useDeleteSeal,
  useSetDefaultSeal,
  useToggleSealActive,
  usePinStatus,
  useSetupPin,
  useVerifyPin,
} from "~/services/business/seal";
import type { CdSealType, CompanySealResponse } from "~/types/common";

const SEAL_TYPE_LABEL: Record<CdSealType, string> = {
  SEAL_TYPE_BIZ: "사업장 직인",
  SEAL_TYPE_CEO: "대표자 인감",
  SEAL_TYPE_CUSTOM: "기타 도장",
};

/** dataURL → File 변환 헬퍼 */
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [meta, b64] = dataUrl.split(",");
  const mime = /data:(.*?);base64/.exec(meta)?.[1] ?? "image/png";
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
};

export const SealManagementPanel = () => {
  const { data: seals = [] } = useSealList();
  const { data: pinStatus } = usePinStatus();
  const createMutation = useCreateSeal();
  const deleteMutation = useDeleteSeal();
  const setDefaultMutation = useSetDefaultSeal();
  const toggleActiveMutation = useToggleSealActive();

  const hasPin = !!pinStatus?.hasPin;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [verified, setVerified] = useState(false);

  // 도장 관리는 진입 시 PIN 인증을 요구한다 — 이후 도장 등록/삭제/기본설정 등 모든 변경을 보호.
  // (PIN 미등록이면 아래 패널에서 등록을 유도하고, 등록에 성공하면 인증된 것으로 간주한다.)
  if (hasPin && !verified) {
    return <SealPinGate onVerified={() => setVerified(true)} />;
  }

  return (
    <div className="space-y-3">
      {/* PIN 미등록 시에만 노출 */}
      {!hasPin && (
        <div className="bg-[#FFF1F1] border border-[#FFD3D3] rounded-xl p-3">
          <div className="text-[11px] text-[#F24747] font-semibold mb-2">
            도장 사용을 위해 PIN 등록이 먼저 필요해요.
          </div>
          <button
            type="button"
            onClick={() => setShowPinModal(true)}
            className="w-full py-2 text-[12px] font-semibold bg-[#FF9934] text-white rounded-full inline-flex items-center justify-center gap-1.5"
          >
            <IconLock className="w-3.5 h-3.5" />
            사용 PIN 등록하기
          </button>
        </div>
      )}

      {/* 등록 도장 목록 + 추가 버튼 */}
      <div className="flex justify-between items-center">
        <div className="text-[13px] font-semibold text-[#111]">
          등록된 도장 ({seals.length})
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="bg-[#FF9934] text-white text-[12px] font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1"
        >
          <IconPlus className="w-3.5 h-3.5" />
          도장 추가
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {seals.length === 0 && (
          <div className="col-span-2 text-center text-[#999] text-[12px] py-8 bg-white rounded-xl border border-[#E9E9E9]">
            아직 등록된 도장이 없어요
            <br />
            <span className="text-[10px]">
              "+ 도장 추가" 버튼으로 시작하세요
            </span>
          </div>
        )}
        {seals.map((s) => (
          <SealCard
            key={s.sealSeq}
            seal={s}
            onSetDefault={() => setDefaultMutation.mutate(s.sealSeq)}
            onToggle={() => toggleActiveMutation.mutate(s.sealSeq)}
            onRemove={() => {
              if (confirm(`"${s.sealName}" 도장을 삭제할까요?`))
                deleteMutation.mutate(s.sealSeq);
            }}
          />
        ))}
      </div>

      {hasPin && (
        <div className="bg-white rounded-xl border border-[#E9E9E9] p-3">
          <div className="text-[12px] font-semibold text-[#111] mb-1 inline-flex items-center gap-1.5">
            <IconLock className="w-3.5 h-3.5 text-[#666]" />
            사용 PIN
          </div>
          <div className="text-[11px] text-[#999] mb-2">
            계약서 발송 시 도장 사용 인증에 쓰여요
          </div>
          <button
            type="button"
            onClick={() => setShowPinModal(true)}
            className="w-full py-2 text-[12px] font-semibold border border-[#E9E9E9] text-[#666] rounded-full"
          >
            PIN 다시 설정
          </button>
        </div>
      )}

      {showAddModal && (
        <AddSealModal
          onClose={() => setShowAddModal(false)}
          onSave={(payload) => {
            const file = dataUrlToFile(
              payload.imageDataUrl,
              `seal_${Date.now()}.png`,
            );
            createMutation.mutate(
              {
                data: {
                  cdSealType: payload.cdSealType,
                  sealName: payload.sealName,
                  // 첫 도장은 무조건 기본으로 설정 — 계약서 도장 필드가 바로 이 도장을 쓰도록.
                  // (백엔드도 첫 도장을 default 로 강제하지만, FE 에서도 명시해 의도를 분명히 한다.)
                  setAsDefault:
                    seals.length === 0 ? true : payload.setAsDefault,
                },
                image: file,
              },
              {
                onSuccess: () => setShowAddModal(false),
                onError: (e: unknown) => {
                  console.error(e);
                  const msg =
                    e instanceof Error ? e.message : "알 수 없는 오류";
                  alert(`도장 등록에 실패했어요\n${msg}`);
                },
              },
            );
          }}
        />
      )}
      {showPinModal && (
        <PinSetupModal
          onClose={() => setShowPinModal(false)}
          onSuccess={() => setVerified(true)}
        />
      )}
    </div>
  );
};

/* ─────────── 도장 관리 진입 PIN 인증 게이트 ─────────── */
const SealPinGate = ({ onVerified }: { onVerified: () => void }) => {
  const { data: pinStatus, refetch } = usePinStatus();
  const verifyMutation = useVerifyPin();
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const failCount = pinStatus?.failCount ?? 0;
  const locked =
    !!pinStatus?.lockedUntil && new Date(pinStatus.lockedUntil) > new Date();

  const submit = async () => {
    if (submitting || locked) return;
    if (!/^\d{6}$/.test(pin)) return alert("PIN 6자리를 입력해 주세요");
    setSubmitting(true);
    try {
      const ok = await verifyMutation.mutateAsync({ pin });
      if (ok) {
        onVerified();
        return;
      }
      setPin("");
      await refetch();
      alert("PIN이 올바르지 않아요");
    } catch {
      setPin("");
      await refetch();
      alert("PIN이 올바르지 않아요");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
      <div className="w-14 h-14 rounded-full bg-[#FFF7E6] flex items-center justify-center mb-4">
        <IconLock className="w-6 h-6 text-[#FF9934]" />
      </div>
      <div className="text-[15px] font-bold text-[#111] mb-1">
        도장 관리 인증
      </div>
      <div className="text-[12px] text-[#666] mb-5">
        도장 등록·삭제를 위해 사용 PIN 6자리를 입력해 주세요
      </div>
      <input
        inputMode="numeric"
        maxLength={6}
        value={pin}
        autoFocus
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        placeholder="● ● ● ● ● ●"
        className="w-full max-w-[240px] px-3 py-3 text-[18px] tracking-[0.4em] text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-2"
      />
      {failCount > 0 && !locked && (
        <div className="text-[11px] text-[#F24747] mb-2">
          {failCount}회 틀렸어요 (5회 실패 시 잠금)
        </div>
      )}
      {locked && (
        <div className="text-[11px] text-[#F24747] mb-2">
          5회 실패 — 잠시 후 다시 시도하세요
        </div>
      )}
      <button
        type="button"
        onClick={submit}
        disabled={submitting || locked}
        className="w-full max-w-[240px] py-2.5 text-[13px] font-semibold bg-[#FF9934] text-white rounded-full disabled:bg-[#E9E9E9] disabled:text-[#999]"
      >
        {submitting ? "인증 중…" : "인증"}
      </button>
    </div>
  );
};

/* ─────────── 도장 카드 ─────────── */
const SealCard = ({
  seal,
  onSetDefault,
  onToggle,
  onRemove,
}: {
  seal: CompanySealResponse;
  onSetDefault: () => void;
  onToggle: () => void;
  onRemove: () => void;
}) => {
  const active = seal.activeYn === "Y";
  const isDefault = seal.defaultYn === "Y";
  return (
    <div
      className={`bg-white rounded-xl border p-3 ${
        active ? "border-[#E9E9E9]" : "border-[#E9E9E9] opacity-60"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold text-[#999]">
          {SEAL_TYPE_LABEL[seal.cdSealType]}
        </div>
        {isDefault && (
          <span className="text-[9px] bg-[#FFF7E6] text-[#FF9934] font-bold rounded px-1.5 py-0.5">
            기본
          </span>
        )}
      </div>
      <div className="aspect-square bg-[#FAFAFA] rounded-lg flex items-center justify-center mb-2 overflow-hidden">
        <img src={seal.imageUrl} className="max-w-[80%] max-h-[80%]" />
      </div>
      <div className="text-[12px] font-semibold text-[#111] truncate">
        {seal.sealName}
      </div>
      <div className="text-[10px] text-[#999] mb-2">
        {seal.dtRegist?.slice(0, 10)}
      </div>
      <div className="flex gap-1">
        {!isDefault && (
          <button
            type="button"
            onClick={onSetDefault}
            className="flex-1 text-[10px] py-1.5 border border-[#FF9934] text-[#FF9934] rounded-full"
          >
            기본
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 text-[10px] py-1.5 border border-[#E9E9E9] text-[#666] rounded-full"
        >
          {active ? "비활성" : "활성"}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="py-1.5 px-2 border border-[#F24747] text-[#F24747] rounded-full inline-flex items-center"
        >
          <IconTrash className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

/* ─────────── 도장 추가 모달 ─────────── */
type AddMethod = "auto" | "draw" | "upload";

const AddSealModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (s: {
    cdSealType: CdSealType;
    sealName: string;
    imageDataUrl: string;
    setAsDefault?: boolean;
  }) => void;
}) => {
  const [method, setMethod] = useState<AddMethod>("auto");
  const [sealType, setSealType] = useState<CdSealType>("SEAL_TYPE_BIZ");
  const [sealName, setSealName] = useState("");
  // setAsDefault toggle UI 미구현 — 추가 시 setter 재활성화
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [setAsDefault, _setSetAsDefault] = useState(false);

  const [text, setText] = useState("");
  const [shape, setShape] = useState<SealShape>("circle");
  const previewDataUrl =
    method === "auto" && text.trim()
      ? generateSealDataUrl({ text, shape })
      : null;

  const drawCanvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  useEffect(() => {
    if (method === "draw") {
      const c = drawCanvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d")!;
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.strokeStyle = "#111";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      setHasDrawn(false);
    }
  }, [method]);
  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const c = drawCanvasRef.current!;
    const rect = c.getBoundingClientRect();
    const sx = c.width / rect.width;
    const sy = c.height / rect.height;
    const cx = "touches" in e ? e.touches[0].clientX : e.clientX;
    const cy = "touches" in e ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };
  const startD = (e: React.MouseEvent | React.TouchEvent) => {
    drawing.current = true;
    const ctx = drawCanvasRef.current!.getContext("2d")!;
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveD = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = drawCanvasRef.current!.getContext("2d")!;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const endD = () => {
    drawing.current = false;
  };
  const clearDraw = () => {
    const c = drawCanvasRef.current;
    if (!c) return;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const [uploadedDataUrl, setUploadedDataUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const handleFile = (f: File | null) => {
    if (!f) return;
    if (!/\.(png|jpg|jpeg)$/i.test(f.name)) {
      alert("PNG/JPG 파일만 올릴 수 있어요");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setUploadedDataUrl(e.target?.result as string);
    reader.readAsDataURL(f);
  };

  const handleSave = async () => {
    if (!sealName.trim()) {
      alert("도장 이름을 입력해 주세요");
      return;
    }
    let imageDataUrl: string | null = null;
    if (method === "auto") {
      if (!text.trim()) return alert("도장에 들어갈 글자를 입력해 주세요");
      imageDataUrl = generateSealDataUrl({ text, shape });
    } else if (method === "draw") {
      if (!hasDrawn) return alert("도장을 그려 주세요");
      const raw = drawCanvasRef.current!.toDataURL("image/png");
      imageDataUrl = await tintCanvasToSealColor(raw);
    } else {
      if (!uploadedDataUrl) return alert("이미지를 올려 주세요");
      imageDataUrl = uploadedDataUrl;
    }
    onSave({
      cdSealType: sealType,
      sealName,
      imageDataUrl: imageDataUrl!,
      setAsDefault,
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-sm p-3 pb-[calc(env(safe-area-inset-bottom)+5.5rem)] md:pb-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[14px] font-bold mb-2">도장 만들기</div>

        <div className="grid grid-cols-3 gap-1.5 mb-2">
          <MethodPill
            active={method === "auto"}
            onClick={() => setMethod("auto")}
            Icon={IconSparkle}
            label="자동생성"
          />
          <MethodPill
            active={method === "draw"}
            onClick={() => setMethod("draw")}
            Icon={IconScribble}
            label="직접 그리기"
          />
          <MethodPill
            active={method === "upload"}
            onClick={() => setMethod("upload")}
            Icon={IconImage}
            label="업로드"
          />
        </div>

        <div className="space-y-1.5 mb-2">
          <div className="grid grid-cols-3 gap-1.5">
            {(
              [
                { v: "SEAL_TYPE_BIZ", label: "직인" },
                { v: "SEAL_TYPE_CEO", label: "인감" },
                { v: "SEAL_TYPE_CUSTOM", label: "기타" },
              ] as { v: CdSealType; label: string }[]
            ).map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setSealType(t.v)}
                className={`text-[11px] py-1.5 rounded-full border ${
                  sealType === t.v
                    ? "border-[#FF9934] bg-[#FFF7E6] text-[#FF9934] font-semibold"
                    : "border-[#E9E9E9] text-[#666]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={sealName}
            onChange={(e) => setSealName(e.target.value)}
            placeholder="도장 이름 (예: 댕댕유치원 직인)"
            className="w-full px-2.5 py-2 text-[12px] border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934]"
          />
        </div>

        {method === "auto" && (
          <div>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="도장 글자 (1~6자)"
              maxLength={6}
              className="w-full px-2.5 py-2 text-[12px] border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-1.5"
            />
            <div className="flex gap-1.5 mb-2">
              {(["circle", "square", "ellipse"] as SealShape[]).map((sh) => (
                <button
                  key={sh}
                  type="button"
                  onClick={() => setShape(sh)}
                  className={`flex-1 text-[11px] py-1.5 rounded-full border ${
                    shape === sh
                      ? "border-[#FF9934] bg-[#FFF7E6] text-[#FF9934] font-semibold"
                      : "border-[#E9E9E9] text-[#666]"
                  }`}
                >
                  {sh === "circle"
                    ? "원형"
                    : sh === "square"
                      ? "사각형"
                      : "타원"}
                </button>
              ))}
            </div>
            <div className="h-28 bg-[#FAFAFA] rounded-lg flex items-center justify-center mb-2 border border-[#E9E9E9]">
              {previewDataUrl ? (
                <img src={previewDataUrl} className="max-h-[90%]" />
              ) : (
                <div className="text-[11px] text-[#999]">미리보기</div>
              )}
            </div>
          </div>
        )}

        {method === "draw" && (
          <div>
            <div className="text-[10px] text-[#666] mb-1">
              빨간 잉크로 자동 변환돼요
            </div>
            <canvas
              ref={drawCanvasRef}
              width={400}
              height={300}
              className="w-full h-32 bg-[#FAFAFA] border border-[#E9E9E9] rounded-lg touch-none"
              onMouseDown={startD}
              onMouseMove={moveD}
              onMouseUp={endD}
              onMouseLeave={endD}
              onTouchStart={startD}
              onTouchMove={moveD}
              onTouchEnd={endD}
            />
            <button
              type="button"
              onClick={clearDraw}
              className="w-full mt-1.5 py-1.5 text-[11px] border border-[#E9E9E9] text-[#666] rounded-full"
            >
              지우기
            </button>
          </div>
        )}

        {method === "upload" && (
          <div>
            <div
              onClick={() => fileRef.current?.click()}
              className="h-28 border-2 border-dashed border-[#E9E9E9] rounded-lg flex items-center justify-center cursor-pointer bg-[#FAFAFA]"
            >
              {uploadedDataUrl ? (
                <img src={uploadedDataUrl} className="max-h-[90%]" />
              ) : (
                <div className="text-center text-[#999] flex flex-col items-center gap-0.5">
                  <IconImage className="w-6 h-6" />
                  <div className="text-[10px]">PNG/JPG 클릭해서 선택</div>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-[2] py-2 text-[12px] font-semibold bg-[#FF9934] text-white rounded-full"
          >
            도장 등록
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const MethodPill = ({
  active,
  onClick,
  Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  Icon: React.ComponentType<{ className?: string; size?: number }>;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`py-2 rounded-lg border flex flex-col items-center gap-0.5 ${
      active
        ? "border-[#FF9934] bg-[#FFF7E6] text-[#FF9934] font-semibold"
        : "border-[#E9E9E9] text-[#666]"
    }`}
  >
    <Icon className="w-4 h-4" />
    <div className="text-[10px]">{label}</div>
  </button>
);

const PinSetupModal = ({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const { data: pinStatus } = usePinStatus();
  const setupMutation = useSetupPin();
  const hasPin = !!pinStatus?.hasPin;
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const handle = () => {
    if (!/^\d{6}$/.test(pin1)) return alert("PIN은 숫자 6자리예요");
    if (pin1 !== pin2) return alert("PIN이 서로 달라요");
    if (hasPin && !/^\d{6}$/.test(currentPin)) {
      return alert("기존 PIN 6자리를 입력해 주세요");
    }
    setupMutation.mutate(
      { newPin: pin1, currentPin: hasPin ? currentPin : undefined },
      {
        onSuccess: () => {
          alert(hasPin ? "PIN을 다시 설정했어요" : "PIN이 등록됐어요");
          onSuccess?.();
          onClose();
        },
        onError: () => alert("PIN 설정 실패. 기존 PIN 이 맞는지 확인해 주세요"),
      },
    );
  };
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center overflow-y-auto px-4 pt-6 pb-[calc(env(safe-area-inset-bottom)+5.5rem)]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[16px] font-bold mb-1">
          {hasPin ? "PIN 다시 설정" : "사용 PIN 등록"}
        </div>
        <div className="text-[12px] text-[#666] mb-4">
          계약서 발송 시 도장 사용을 인증하는 6자리 숫자예요
        </div>
        {hasPin && (
          <input
            inputMode="numeric"
            maxLength={6}
            value={currentPin}
            onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
            placeholder="기존 PIN 6자리"
            className="w-full px-3 py-2.5 text-[14px] tracking-widest text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-2"
          />
        )}
        <input
          inputMode="numeric"
          maxLength={6}
          value={pin1}
          onChange={(e) => setPin1(e.target.value.replace(/\D/g, ""))}
          placeholder={hasPin ? "새 PIN 6자리" : "PIN 6자리"}
          className="w-full px-3 py-2.5 text-[14px] tracking-widest text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-2"
        />
        <input
          inputMode="numeric"
          maxLength={6}
          value={pin2}
          onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
          placeholder="확인을 위해 한 번 더"
          className="w-full px-3 py-2.5 text-[14px] tracking-widest text-center border border-[#E9E9E9] rounded-lg focus:outline-none focus:border-[#FF9934] mb-3"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-[12px] border border-[#E9E9E9] text-[#666] rounded-full"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handle}
            className="flex-[2] py-2.5 text-[12px] font-semibold bg-[#FF9934] text-white rounded-full"
          >
            등록
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
